// src/hooks/useInsightsPrefetch.js
import { useCallback, useRef } from 'react';
import insightsAPI from '../api/insights';

/**
 * Custom hook for insights prefetching
 * Provides a clean interface for triggering background prefetch
 */
const useInsightsPrefetch = () => {
  const prefetchTimeoutRef = useRef(null);
  const prefetchAttemptedRef = useRef(false);

  /**
   * Trigger insights prefetch with retry logic
   * @param {Object} options - Prefetch options
   * @param {Number} options.delay - Delay before prefetch (ms)
   * @param {Number} options.maxRetries - Maximum retry attempts
   * @param {Boolean} options.force - Force prefetch even if already attempted
   * @returns {Promise} Prefetch result
   */
  const triggerPrefetch = useCallback(async (options = {}) => {
    const {
      delay = 1000, // 1 second delay by default
      maxRetries = 2,
      force = false
    } = options;

    // Prevent multiple prefetch attempts unless forced
    if (prefetchAttemptedRef.current && !force) {
      console.log('🔄 Insights prefetch already attempted, skipping');
      return { status: 'skipped', reason: 'already_attempted' };
    }

    // Check if prefetch is enabled
    if (!insightsAPI.isPrefetchEnabled()) {
      console.log('📋 Insights prefetch disabled by configuration');
      return { status: 'disabled', reason: 'configuration' };
    }

    // Clear any existing timeout
    if (prefetchTimeoutRef.current) {
      clearTimeout(prefetchTimeoutRef.current);
    }

    // Mark as attempted
    prefetchAttemptedRef.current = true;

    return new Promise((resolve) => {
      prefetchTimeoutRef.current = setTimeout(async () => {
        console.log('🚀 Starting insights prefetch...');
        
        let attempt = 0;
        let lastError = null;

        while (attempt < maxRetries) {
          try {
            attempt++;
            console.log(`📡 Prefetch attempt ${attempt}/${maxRetries}`);
            
            const result = await insightsAPI.primeInsights();
            
            if (result.status === 'priming' || result.status === 'already_in_flight') {
              console.log('✅ Insights prefetch successful:', result.message);
              resolve({ 
                success: true, 
                status: result.status, 
                message: result.message,
                attempts: attempt 
              });
              return;
            } else if (result.nonBlocking) {
              // Non-blocking error from API service
              console.warn('⚠️ Insights prefetch failed (non-blocking):', result.message);
              resolve({ 
                success: false, 
                status: 'failed_non_blocking', 
                message: result.message,
                attempts: attempt 
              });
              return;
            }

          } catch (error) {
            lastError = error;
            console.warn(`⚠️ Prefetch attempt ${attempt} failed:`, error.message);
            
            // If not the last attempt, wait before retrying
            if (attempt < maxRetries) {
              const retryDelay = 2000 * attempt; // Exponential backoff
              console.log(`⏳ Retrying prefetch in ${retryDelay}ms...`);
              await new Promise(resolve => setTimeout(resolve, retryDelay));
            }
          }
        }

        // All attempts failed
        console.error('❌ All prefetch attempts failed:', lastError?.message);
        resolve({ 
          success: false, 
          status: 'failed', 
          error: lastError?.message || 'Unknown error',
          attempts: attempt 
        });

      }, delay);
    });
  }, []);

  /**
   * Cancel any pending prefetch
   */
  const cancelPrefetch = useCallback(() => {
    if (prefetchTimeoutRef.current) {
      clearTimeout(prefetchTimeoutRef.current);
      prefetchTimeoutRef.current = null;
      console.log('🚫 Insights prefetch cancelled');
    }
  }, []);

  /**
   * Reset prefetch state (allows new prefetch attempts)
   */
  const resetPrefetch = useCallback(() => {
    prefetchAttemptedRef.current = false;
    cancelPrefetch();
    console.log('🔄 Insights prefetch state reset');
  }, [cancelPrefetch]);

  /**
   * Quick prefetch with default settings (for login)
   */
  const prefetchOnLogin = useCallback(() => {
    return triggerPrefetch({
      delay: 500, // Quick start after login
      maxRetries: 3 // More retries for login prefetch
    });
  }, [triggerPrefetch]);

  /**
   * Get prefetch status
   */
  const getPrefetchStatus = useCallback(() => {
    return {
      attempted: prefetchAttemptedRef.current,
      pending: !!prefetchTimeoutRef.current,
      enabled: insightsAPI.isPrefetchEnabled(),
      config: insightsAPI.getPrefetchConfig()
    };
  }, []);

  // Cleanup on unmount
  const cleanup = useCallback(() => {
    cancelPrefetch();
  }, [cancelPrefetch]);

  return {
    triggerPrefetch,
    cancelPrefetch,
    resetPrefetch,
    prefetchOnLogin,
    getPrefetchStatus,
    cleanup
  };
};

export default useInsightsPrefetch;
